import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader, SEO, SubscriptionGate } from '../../components/common';
import { IoArrowBack } from 'react-icons/io5';
import { fetchBucketStocks, fetchBuckets, BucketDetail as BucketDetailType, BucketStock } from '../../api/buckets';
import { useSubscriptionGate } from '../../hooks/useSubscriptionGate';

const getRatingColor = (rating: string | null, colors: any) => {
  switch (rating) {
    case 'strong_buy':
      return colors.green;
    case 'buy':
      return '#4CAF50';
    case 'hold':
      return colors.grayScale500;
    case 'sell':
      return '#FF9800';
    case 'strong_sell':
      return colors.alertColor;
    default:
      return colors.grayScale400;
  }
};

const getRatingLabel = (rating: string | null) => {
  switch (rating) {
    case 'strong_buy':
      return 'Strong Buy';
    case 'buy':
      return 'Buy';
    case 'hold':
      return 'Hold';
    case 'sell':
      return 'Sell';
    case 'strong_sell':
      return 'Strong Sell';
    default:
      return 'N/A';
  }
};

const BucketDetail: React.FC = () => {
  const { bucketName } = useParams<{ bucketName: string }>();
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const allBucketsGate = useSubscriptionGate('all_buckets');

  const [bucketData, setBucketData] = useState<BucketDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucketIndex, setBucketIndex] = useState<number>(0);

  const decodedBucketName = bucketName ? decodeURIComponent(bucketName) : '';

  useEffect(() => {
    const loadBucketStocks = async () => {
      if (!decodedBucketName) return;

      try {
        setLoading(true);
        setError(null);

        // Load bucket list to determine this bucket's index in its category
        const [data, allBuckets] = await Promise.all([
          fetchBucketStocks(decodedBucketName),
          fetchBuckets(),
        ]);
        setBucketData(data);

        // Find this bucket's position in its category
        const thisBucket = allBuckets.find(b => b.bucket_name === decodedBucketName);
        if (thisBucket) {
          const sameCategoryBuckets = allBuckets.filter(b => b.bucket_category === thisBucket.bucket_category);
          const idx = sameCategoryBuckets.findIndex(b => b.bucket_name === decodedBucketName);
          setBucketIndex(idx >= 0 ? idx : 0);
        }
      } catch (err) {
        console.error('Failed to fetch bucket stocks:', err);
        setError('Failed to load bucket data');
      } finally {
        setLoading(false);
      }
    };
    loadBucketStocks();
  }, [decodedBucketName]);

  // Check access: free users can only view first 2 buckets per category
  const isLocked = !allBucketsGate.allowed && bucketIndex >= 2;

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO
        title={`${decodedBucketName} Stocks`}
        description={`Explore the ${decodedBucketName} investment bucket on Yantrai.${bucketData ? ` ${bucketData.stocks.length} stocks with ratings, valuations, and analysis.` : ''}`}
        canonical={`/buckets/${encodeURIComponent(decodedBucketName)}`}
      />
      <div className="lg:max-w-4xl lg:mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:opacity-80 transition-opacity"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(IoArrowBack as any, { size: 20, color: colors.textColor })}
          </button>
          <div>
            <CText type="B20" color={colors.textColor}>
              {decodedBucketName}
            </CText>
            {bucketData && (
              <CText type="r14" color={colors.grayScale500}>
                {bucketData.stocks.length} stocks in this bucket
              </CText>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <CLoader />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <CText type="M16" color={colors.alertColor}>
              {error}
            </CText>
            <button
              className="mt-4 px-4 py-2 rounded-lg"
              style={{ backgroundColor: colors.primary, color: colors.white }}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Subscription Gate for locked buckets */}
        {!loading && !error && isLocked && (
          <SubscriptionGate
            allowed={false}
            requiredTier="pro"
            reason="Upgrade to Pro to access all investment buckets"
            mode="overlay"
          >
            <div />
          </SubscriptionGate>
        )}

        {/* Empty State */}
        {!loading && !error && !isLocked && bucketData?.stocks.length === 0 && (
          <div className="text-center py-12">
            <CText type="M16" color={colors.grayScale500}>
              No stocks in this bucket yet
            </CText>
            <CText type="r14" color={colors.grayScale400} style={{ marginTop: 8 }}>
              Stocks will be added by the discovery agent
            </CText>
          </div>
        )}

        {/* Stocks List */}
        {!loading && !error && !isLocked && bucketData && bucketData.stocks.length > 0 && (
          <div className="flex flex-col gap-3">
            {bucketData.stocks.map((stock, index) => (
              <StockCard
                key={`${stock.stock_name}-${index}`}
                stock={stock}
                colors={colors}
                onClick={() => navigate(`/company/${encodeURIComponent(stock.stock_name)}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface StockCardProps {
  stock: BucketStock;
  colors: any;
  onClick: () => void;
}

const StockCard: React.FC<StockCardProps> = ({ stock, colors, onClick }) => {
  const financialInfo = stock.financial_info || {};
  const whyIncluded = stock.analysis || stock.query || null;

  return (
    <div
      className="p-4 rounded-xl border hover:shadow-lg transition-all cursor-pointer"
      style={{
        backgroundColor: colors.inputBackground,
        borderColor: colors.borderColor,
      }}
      onClick={onClick}
    >
      {/* Top: Name + Symbol + Rating */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CText type="M14" color={colors.textColor}>
            {stock.stock_name}
          </CText>
          {stock.stock_symbol && (
            <CText type="r12" color={colors.grayScale400}>
              {stock.stock_symbol}
            </CText>
          )}
        </div>
        {stock.rating && (
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium shrink-0"
            style={{
              backgroundColor: getRatingColor(stock.rating, colors) + '20',
              color: getRatingColor(stock.rating, colors),
            }}
          >
            {getRatingLabel(stock.rating)}
          </span>
        )}
      </div>

      {/* Middle: Why included */}
      {whyIncluded && (
        <CText
          type="r12"
          color={colors.grayScale500}
          style={{
            marginBottom: 10,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {whyIncluded}
        </CText>
      )}

      {/* Bottom: Valuation metrics bar */}
      <div
        className="pt-2 flex flex-wrap gap-x-6 gap-y-1"
        style={{ borderTop: `1px solid ${colors.borderColor}` }}
      >
        {financialInfo.price_to_earnings != null && (
          <MetricItem label="P/E" value={financialInfo.price_to_earnings.toFixed(1)} colors={colors} />
        )}
        {financialInfo.market_cap_to_sales != null && (
          <MetricItem label="MCap/Sales" value={financialInfo.market_cap_to_sales.toFixed(1)} colors={colors} />
        )}
        {financialInfo.price_to_book != null && (
          <MetricItem label="P/B" value={financialInfo.price_to_book.toFixed(1)} colors={colors} />
        )}
        {financialInfo.ev_to_ebitda != null && (
          <MetricItem label="EV/EBITDA" value={financialInfo.ev_to_ebitda.toFixed(1)} colors={colors} />
        )}
        {financialInfo.market_cap != null && (
          <MetricItem label="Market Cap" value={formatMarketCap(financialInfo.market_cap)} colors={colors} />
        )}
      </div>
    </div>
  );
};

const MetricItem: React.FC<{ label: string; value: string; colors: any }> = ({ label, value, colors }) => (
  <div className="flex items-center gap-1.5">
    <CText type="r10" color={colors.grayScale400}>
      {label}
    </CText>
    <CText type="M12" color={colors.textColor}>
      {value}
    </CText>
  </div>
);

const formatMarketCap = (value: number): string => {
  if (value >= 100000) {
    return `${(value / 100000).toFixed(1)}L Cr`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K Cr`;
  }
  return `${value} Cr`;
};

export default BucketDetail;
